import { ReactNode } from 'react';
import { Lang } from './i18n';

interface LegalContent {
    mentions: () => ReactNode;
    privacy: () => ReactNode;
    cgv: () => ReactNode;
}

const legalContent: Record<Lang, LegalContent> = {
    fr: {
        mentions: () => (
            <>
                <h1>Mentions Légales</h1>
                <p><em>Dernière mise à jour : janvier 2026</em></p>

                <h2>Éditeur du site</h2>
                <p>
                    <strong>Bitek</strong> — Nicolas Cordeiro<br />
                    SIREN : 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, France<br />
                    Email : <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Directeur de la publication</h2>
                <p>Nicolas Cordeiro</p>

                <h2>Hébergement</h2>
                <p>
                    <strong>Cloudflare, Inc.</strong><br />
                    101 Townsend Street<br />
                    San Francisco, CA 94107<br />
                    États-Unis<br />
                    <a href="https://www.cloudflare.com" target="_blank" rel="noopener noreferrer">www.cloudflare.com</a>
                </p>

                <h2>Propriété intellectuelle</h2>
                <p>
                    L'ensemble du contenu de ce site (textes, images, logos, code source) est la propriété
                    exclusive de Bitek (Nicolas Cordeiro), sauf mention contraire. Toute reproduction, même partielle,
                    est interdite sans autorisation préalable.
                </p>
            </>
        ),
        privacy: () => (
            <>
                <h1>Politique de Confidentialité</h1>
                <p><em>Dernière mise à jour : janvier 2026</em></p>

                <h2>Responsable du traitement</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    Email : <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Données collectées</h2>
                <p>Nous collectons les données suivantes :</p>
                <ul>
                    <li><strong>Adresse email</strong> : fournie via l'authentification Google OAuth</li>
                    <li><strong>Données de liens</strong> : URLs raccourcies, destinations, descriptions créées par l'utilisateur</li>
                    <li><strong>Données de paiement</strong> : traitées par Polar (via Stripe), nous ne stockons pas vos informations de carte bancaire</li>
                </ul>

                <h2>Finalités du traitement</h2>
                <ul>
                    <li>Création et gestion de votre compte utilisateur</li>
                    <li>Fourniture du service de raccourcissement d'URL</li>
                    <li>Traitement des paiements pour l'offre Pro</li>
                    <li>Communication relative à votre compte (support, facturation)</li>
                </ul>

                <h2>Base légale</h2>
                <p>
                    Le traitement de vos données est fondé sur l'exécution du contrat (fourniture du service)
                    et votre consentement lors de l'authentification.
                </p>

                <h2>Durée de conservation</h2>
                <p>
                    Vos données sont conservées tant que votre compte est actif. En cas de suppression de compte,
                    vos données seront effacées sous 30 jours, sauf obligations légales de conservation.
                </p>

                <h2>Vos droits (RGPD)</h2>
                <p>Conformément au RGPD, vous disposez des droits suivants :</p>
                <ul>
                    <li><strong>Droit d'accès</strong> : obtenir une copie de vos données</li>
                    <li><strong>Droit de rectification</strong> : corriger vos données inexactes</li>
                    <li><strong>Droit à l'effacement</strong> : supprimer vos données ("droit à l'oubli")</li>
                    <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
                    <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
                </ul>
                <p>
                    Pour exercer ces droits, contactez-nous à : <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Cookies</h2>
                <p>
                    Ce site utilise uniquement des cookies techniques essentiels au fonctionnement du service
                    (authentification, préférences de langue). Aucun cookie publicitaire ou de suivi n'est utilisé.
                </p>

                <h2>Transferts de données</h2>
                <p>
                    Vos données peuvent être transférées vers des serveurs situés hors de l'Union Européenne
                    (Cloudflare, Stripe). Ces transferts sont encadrés par des garanties appropriées
                    (clauses contractuelles types, certifications).
                </p>

                <h2>Réclamation</h2>
                <p>
                    En cas de litige, vous pouvez déposer une réclamation auprès de la CNIL :
                    <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
                </p>
            </>
        ),
        cgv: () => (
            <>
                <h1>Conditions Générales de Vente</h1>
                <p><em>Dernière mise à jour : janvier 2026</em></p>

                <h2>Article 1 – Objet</h2>
                <p>
                    Les présentes Conditions Générales de Vente (CGV) régissent la vente de l'offre "Pro"
                    du service ShortURL, proposé par Nicolas Cordeiro, entrepreneur individuel.
                </p>

                <h2>Article 2 – Identification du vendeur</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    SIREN : 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, France<br />
                    Email : <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Article 3 – Produit</h2>
                <p>
                    L'offre "Pro" est un achat unique (pas d'abonnement) au prix de <strong>5,99€</strong>.
                    Elle donne accès à vie aux fonctionnalités suivantes :
                </p>
                <ul>
                    <li>Création de liens illimités</li>
                    <li>Support prioritaire</li>
                    <li>Accès anticipé aux nouvelles fonctionnalités</li>
                </ul>

                <h2>Article 4 – Prix et paiement</h2>
                <p>
                    Le prix affiché est de 5,99€ hors taxes. Les taxes applicables (TVA ou équivalent)
                    seront calculées lors du paiement en fonction de votre pays de résidence.
                    Le montant final, taxes comprises, sera affiché avant confirmation. Le paiement
                    s'effectue en ligne par carte bancaire via notre prestataire Polar (qui utilise Stripe).
                    Le paiement est sécurisé et conforme aux normes PCI-DSS.
                </p>

                <h2>Article 5 – Livraison</h2>
                <p>
                    L'accès aux fonctionnalités Pro est délivré immédiatement après confirmation du paiement.
                    Aucun délai de livraison n'est applicable.
                </p>

                <h2>Article 6 – Droit de rétractation</h2>
                <p>
                    Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation
                    ne peut être exercé pour les contrats de fourniture de contenu numérique non fourni sur
                    un support matériel dont l'exécution a commencé après accord préalable exprès du consommateur
                    et renoncement exprès à son droit de rétractation.
                </p>
                <p>
                    <strong>En procédant à l'achat, vous reconnaissez que l'accès aux fonctionnalités Pro est
                        immédiat et que vous renoncez expressément à votre droit de rétractation.</strong>
                </p>

                <h2>Article 7 – Garantie et responsabilité</h2>
                <p>
                    Le service est fourni "en l'état". Nous nous engageons à maintenir le service dans la
                    mesure du possible, mais ne garantissons pas une disponibilité 100% continue.
                </p>

                <h2>Article 8 – Données personnelles</h2>
                <p>
                    Les données collectées sont traitées conformément à notre{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = 'privacy'; window.location.reload(); }}>
                        Politique de Confidentialité
                    </a>.
                </p>

                <h2>Article 9 – Droit applicable</h2>
                <p>
                    Les présentes CGV sont soumises au droit français. En cas de litige, les tribunaux
                    français seront seuls compétents.
                </p>

                <h2>Article 10 – Contact</h2>
                <p>
                    Pour toute question relative à ces CGV, contactez-nous à :{' '}
                    <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>
            </>
        ),
    },
    en: {
        mentions: () => (
            <>
                <h1>Legal Notice</h1>
                <p><em>Last updated: January 2026</em></p>

                <h2>Site Publisher</h2>
                <p>
                    <strong>Bitek</strong> — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, France<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Publication Director</h2>
                <p>Nicolas Cordeiro</p>

                <h2>Hosting</h2>
                <p>
                    <strong>Cloudflare, Inc.</strong><br />
                    101 Townsend Street<br />
                    San Francisco, CA 94107<br />
                    United States<br />
                    <a href="https://www.cloudflare.com" target="_blank" rel="noopener noreferrer">www.cloudflare.com</a>
                </p>

                <h2>Intellectual Property</h2>
                <p>
                    All content on this site (texts, images, logos, source code) is the exclusive property
                    of Bitek (Nicolas Cordeiro), unless otherwise stated. Any reproduction, even partial,
                    is prohibited without prior authorization.
                </p>
            </>
        ),
        privacy: () => (
            <>
                <h1>Privacy Policy</h1>
                <p><em>Last updated: January 2026</em></p>

                <h2>Data Controller</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Collected Data</h2>
                <p>We collect the following data:</p>
                <ul>
                    <li><strong>Email address</strong>: provided via Google OAuth authentication</li>
                    <li><strong>Link data</strong>: shortened URLs, destinations, descriptions created by the user</li>
                    <li><strong>Payment data</strong>: processed by Polar (via Stripe), we do not store your credit card information</li>
                </ul>

                <h2>Purposes of Processing</h2>
                <ul>
                    <li>Creation and management of your user account</li>
                    <li>Provision of the URL shortening service</li>
                    <li>Payment processing for the Pro offer</li>
                    <li>Communication related to your account (support, billing)</li>
                </ul>

                <h2>Legal Basis</h2>
                <p>
                    The processing of your data is based on the performance of the contract (provision of the service)
                    and your consent during authentication.
                </p>

                <h2>Retention Period</h2>
                <p>
                    Your data is kept as long as your account is active. In case of account deletion,
                    your data will be erased within 30 days, unless there are legal retention obligations.
                </p>

                <h2>Your Rights (GDPR)</h2>
                <p>In accordance with the GDPR, you have the following rights:</p>
                <ul>
                    <li><strong>Right of access</strong>: obtain a copy of your data</li>
                    <li><strong>Right to rectification</strong>: correct your inaccurate data</li>
                    <li><strong>Right to erasure</strong>: delete your data ("right to be forgotten")</li>
                    <li><strong>Right to portability</strong>: receive your data in a structured format</li>
                    <li><strong>Right to object</strong>: object to the processing of your data</li>
                </ul>
                <p>
                    To exercise these rights, contact us at: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Cookies</h2>
                <p>
                    This site uses only technical cookies essential for the operation of the service
                    (authentication, language preferences). No advertising or tracking cookies are used.
                </p>

                <h2>Data Transfers</h2>
                <p>
                    Your data may be transferred to servers located outside the European Union
                    (Cloudflare, Stripe). These transfers are covered by appropriate safeguards
                    (standard contractual clauses, certifications).
                </p>

                <h2>Claims</h2>
                <p>
                    In case of dispute, you can file a claim with the CNIL: <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
                </p>
            </>
        ),
        cgv: () => (
            <>
                <h1>Terms of Sale</h1>
                <p><em>Last updated: January 2026</em></p>

                <h2>Article 1 – Object</h2>
                <p>
                    These Terms of Sale govern the sale of the "Pro" offer of the ShortURL service,
                    proposed by Nicolas Cordeiro, individual entrepreneur.
                </p>

                <h2>Article 2 – Seller Identification</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, France<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Article 3 – Product</h2>
                <p>
                    The "Pro" offer is a one-time purchase (no subscription) at the price of <strong>5.99€</strong>.
                    It gives lifetime access to the following features:
                </p>
                <ul>
                    <li>Unlimited link creation</li>
                    <li>Priority support</li>
                    <li>Early access to new features</li>
                </ul>

                <h2>Article 4 – Price and Payment</h2>
                <p>
                    The displayed price is 5.99€ before tax. Applicable taxes (VAT or equivalent)
                    will be calculated at checkout based on your country of residence.
                    The final amount, including taxes, will be displayed before confirmation. Payment is
                    made online by credit card via our provider Polar (using Stripe).
                    Payment is secure and PCI-DSS compliant.
                </p>

                <h2>Article 5 – Delivery</h2>
                <p>
                    Access to Pro features is delivered immediately after payment confirmation.
                    No delivery time is applicable.
                </p>

                <h2>Article 6 – Right of Withdrawal</h2>
                <p>
                    In accordance with consumer law, the right of withdrawal cannot be exercised for contracts
                    for the supply of digital content not supplied on a tangible medium whose execution has begun
                    after the consumer's prior express agreement and express waiver of their right of withdrawal.
                </p>
                <p>
                    <strong>By proceeding with the purchase, you acknowledge that access to Pro features is
                        immediate and that you expressly waive your right of withdrawal.</strong>
                </p>

                <h2>Article 7 – Warranty and Liability</h2>
                <p>
                    The service is provided "as is". We are committed to maintaining the service to the
                    extent possible, but do not guarantee 100% continuous availability.
                </p>

                <h2>Article 8 – Personal Data</h2>
                <p>
                    Collected data is processed in accordance with our{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = 'privacy'; window.location.reload(); }}>
                        Privacy Policy
                    </a>.
                </p>

                <h2>Article 9 – Applicable Law</h2>
                <p>
                    These Terms are subject to French law. In case of dispute, French courts will be solely competent.
                </p>

                <h2>Article 10 – Contact</h2>
                <p>
                    For any questions regarding these Terms, contact us at:{' '}
                    <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>
            </>
        ),
    },
    es: {
        mentions: () => (
            <>
                <h1>Aviso Legal</h1>
                <p><em>Última actualización: enero 2026</em></p>

                <h2>Editor del sitio</h2>
                <p>
                    <strong>Bitek</strong> — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, Francia<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Director de publicación</h2>
                <p>Nicolas Cordeiro</p>

                <h2>Alojamiento</h2>
                <p>
                    <strong>Cloudflare, Inc.</strong><br />
                    101 Townsend Street<br />
                    San Francisco, CA 94107<br />
                    Estados Unidos<br />
                    <a href="https://www.cloudflare.com" target="_blank" rel="noopener noreferrer">www.cloudflare.com</a>
                </p>

                <h2>Propiedad intelectual</h2>
                <p>
                    Todo el contenido de este sitio (textos, imágenes, logotipos, código fuente) es propiedad exclusiva de Bitek (Nicolas Cordeiro), salvo que se indique lo contrario. Cualquier reproducción, incluso parcial, está prohibida sin autorización previa.
                </p>
            </>
        ),
        privacy: () => (
            <>
                <h1>Política de Privacidad</h1>
                <p><em>Última actualización: enero 2026</em></p>

                <h2>Responsable del tratamiento</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Datos recopilados</h2>
                <p>Recopilamos los siguientes datos:</p>
                <ul>
                    <li><strong>Dirección de correo electrónico</strong>: proporcionada a través de la autenticación de Google OAuth</li>
                    <li><strong>Datos de enlace</strong>: URLs acortadas, destinos, descripciones creadas por el usuario</li>
                    <li><strong>Datos de pago</strong>: procesados por Polar (a través de Stripe), no almacenamos su información de tarjeta de crédito</li>
                </ul>

                <h2>Finalidad del tratamiento</h2>
                <ul>
                    <li>Creación y gestión de su cuenta de usuario</li>
                    <li>Prestación del servicio de acortamiento de URL</li>
                    <li>Procesamiento de pagos para la oferta Pro</li>
                    <li>Comunicación relacionada con su cuenta (soporte, facturación)</li>
                </ul>

                <h2>Base legal</h2>
                <p>
                    El tratamiento de sus datos se basa en la ejecución del contrato (prestación del servicio) y su consentimiento durante la autenticación.
                </p>

                <h2>Periodo de conservación</h2>
                <p>
                    Sus datos se conservan mientras su cuenta esté activa. En caso de eliminación de la cuenta, sus datos se borrarán en un plazo de 30 días, salvo obligaciones legales de conservación.
                </p>

                <h2>Sus derechos (RGPD)</h2>
                <p>De conformidad con el RGPD, usted tiene los siguientes derechos:</p>
                <ul>
                    <li><strong>Derecho de acceso</strong>: obtener una copia de sus datos</li>
                    <li><strong>Derecho de rectificación</strong>: corregir sus datos inexactos</li>
                    <li><strong>Derecho de supresión</strong>: eliminar sus datos ("derecho al olvido")</li>
                    <li><strong>Derecho a la portabilidad</strong>: recibir sus datos en un formato estructurado</li>
                    <li><strong>Derecho de oposición</strong>: oponerse al tratamiento de sus datos</li>
                </ul>
                <p>
                    Para ejercer estos derechos, contáctenos en: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Cookies</h2>
                <p>
                    Este sitio utiliza únicamente cookies técnicas esenciales para el funcionamiento del servicio (autenticación, preferencias de idioma). No se utilizan cookies publicitarias ni de seguimiento.
                </p>

                <h2>Transferencias de datos</h2>
                <p>
                    Sus datos pueden ser transferidos a servidores ubicados fuera de la Unión Europea (Cloudflare, Stripe). Estas transferencias están cubiertas por garantías adecuadas (cláusulas contractuales estándar, certificaciones).
                </p>

                <h2>Reclamación</h2>
                <p>
                    En caso de disputa, puede presentar una reclamación ante la CNIL: <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
                </p>
            </>
        ),
        cgv: () => (
            <>
                <h1>Condiciones Generales de Venta</h1>
                <p><em>Última actualización: enero 2026</em></p>

                <h2>Artículo 1 – Objeto</h2>
                <p>
                    Estas Condiciones Generales de Venta (CGV) rigen la venta de la oferta "Pro" del servicio ShortURL, propuesto por Nicolas Cordeiro, empresario individual.
                </p>

                <h2>Artículo 2 – Identificación del vendedor</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, Francia<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Artículo 3 – Producto</h2>
                <p>
                    La oferta "Pro" es una compra única (sin suscripción) al precio de <strong>5,99€</strong>.
                    Da acceso de por vida a las siguientes funcionalidades:
                </p>
                <ul>
                    <li>Creación de enlaces ilimitados</li>
                    <li>Soporte prioritario</li>
                    <li>Acceso anticipado a nuevas funcionalidades</li>
                </ul>

                <h2>Artículo 4 – Precio y pago</h2>
                <p>
                    El precio mostrado es de 5,99€ antes de impuestos. Los impuestos aplicables (IVA o equivalente)
                    se calcularán en el momento del pago según su país de residencia.
                    El monto final, impuestos incluidos, se mostrará antes de la confirmación. El pago
                    se realiza en línea mediante tarjeta bancaria a través de nuestro proveedor Polar
                    (utilizando Stripe). El pago es seguro y cumple con las normas PCI-DSS.
                </p>

                <h2>Artículo 5 – Entrega</h2>
                <p>
                    El acceso a las funcionalidades Pro se entrega inmediatamente después de la confirmación del pago. No se aplica ningún plazo de entrega.
                </p>

                <h2>Artículo 6 – Derecho de desistimiento</h2>
                <p>
                    De conformidad con la ley del consumidor, el derecho de desistimiento no puede ejercerse para contratos de suministro de contenido digital no suministrado en un soporte material cuya ejecución haya comenzado tras el acuerdo previo expreso del consumidor y la renuncia expresa a su derecho de desistimiento.
                </p>

                <h2>Artículo 7 – Garantía y responsabilidad</h2>
                <p>
                    El servicio se proporciona "tal cual". Nos comprometemos a mantener el servicio en la medida de lo posible, pero no garantizamos una disponibilidad continua del 100%.
                </p>

                <h2>Artículo 8 – Datos personales</h2>
                <p>
                    Los datos recopilados se procesan de conformidad con nuestra{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = 'privacy'; window.location.reload(); }}>
                        Política de Privacidad
                    </a>.
                </p>

                <h2>Artículo 9 – Ley aplicable</h2>
                <p>
                    Estas CGV están sujetas a la ley francesa. En caso de litigio, los tribunales franceses serán los únicos competentes.
                </p>
            </>
        ),
    },
    pt: {
        mentions: () => (
            <>
                <h1>Aviso Legal</h1>
                <p><em>Última atualização: janeiro 2026</em></p>

                <h2>Editor do site</h2>
                <p>
                    <strong>Bitek</strong> — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, França<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>
                <h2>Diretor de publicação</h2>
                <p>Nicolas Cordeiro</p>

                <h2>Hospedagem</h2>
                <p>
                    <strong>Cloudflare, Inc.</strong><br />
                    101 Townsend Street<br />
                    San Francisco, CA 94107<br />
                    Estados Unidos<br />
                    <a href="https://www.cloudflare.com" target="_blank" rel="noopener noreferrer">www.cloudflare.com</a>
                </p>

                <h2>Propriedade intelectual</h2>
                <p>
                    Todo o conteúdo deste site (textos, imagens, logotipos, código fonte) é propriedade exclusiva da Bitek (Nicolas Cordeiro), salvo indicação em contrário. Qualquer reprodução, mesmo parcial, é proibida sem autorização prévia.
                </p>
            </>
        ),
        privacy: () => (
            <>
                <h1>Política de Privacidade</h1>
                <p><em>Última atualização: janeiro 2026</em></p>

                <h2>Responsável pelo tratamento</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Dados coletados</h2>
                <p>Coletamos os seguintes dados:</p>
                <ul>
                    <li><strong>Endereço de e-mail</strong>: fornecido via autenticação Google OAuth</li>
                    <li><strong>Dados de link</strong>: URLs encurtadas, destinos, descrições criadas pelo usuário</li>
                    <li><strong>Dados de pagamento</strong>: processados pela Polar (via Stripe), não armazenamos suas informações de cartão de crédito</li>
                </ul>

                <h2>Finalidades do tratamento</h2>
                <ul>
                    <li>Criação e gestão da sua conta de utilizador</li>
                    <li>Fornecimento do serviço de encurtamento de URL</li>
                    <li>Processamento de pagamentos para a oferta Pro</li>
                    <li>Comunicação relacionada com a sua conta (suporte, faturação)</li>
                </ul>

                <h2>Base legal</h2>
                <p>
                    O tratamento dos seus dados baseia-se na execução do contrato (fornecimento do serviço) e no seu consentimento durante a autenticação.
                </p>

                <h2>Prazo de conservação</h2>
                <p>
                    Os seus dados são mantidos enquanto a sua conta estiver ativa. Em caso de eliminação da conta, os seus dados serão apagados no prazo de 30 dias, salvo obrigações legais de conservação.
                </p>

                <h2>Os seus direitos (RGPD)</h2>
                <p>De acordo com o RGPD, tem os seguintes direitos:</p>
                <ul>
                    <li><strong>Direito de acesso</strong>: obter uma cópia dos seus dados</li>
                    <li><strong>Direito de retificação</strong>: corrigir os seus dados inexatos</li>
                    <li><strong>Direito ao apagamento</strong>: eliminar os seus dados ("direito a ser esquecido")</li>
                    <li><strong>Direito à portabilidade</strong>: receber os seus dados num formato estruturado</li>
                    <li><strong>Direito de oposição</strong>: opor-se ao tratamento dos seus dados</li>
                </ul>
                <p>
                    Para exercer estes direitos, contacte-nos em: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Cookies</h2>
                <p>
                    Este site utiliza apenas cookies técnicos essenciais para o funcionamento do serviço (autenticação, preferências de idioma). Não são utilizados cookies publicitários ou de rastreamento.
                </p>

                <h2>Transferência de dados</h2>
                <p>
                    Os seus dados podem ser transferidos para servidores localizados fora da União Europeia (Cloudflare, Stripe). Estas transferências são cobertas por garantias adequadas (cláusulas contratuais-tipo, certificações).
                </p>

                <h2>Reclamação</h2>
                <p>
                    Em caso de litígio, pode apresentar uma reclamação junto da CNIL: <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
                </p>
            </>
        ),
        cgv: () => (
            <>
                <h1>Condições Gerais de Venda</h1>
                <p><em>Última atualização: janeiro 2026</em></p>

                <h2>Artigo 1 – Objeto</h2>
                <p>
                    Estas Condições Gerais de Venda (CGV) regem a venda da oferta "Pro" do serviço ShortURL, proposto por Nicolas Cordeiro, empresário individual.
                </p>

                <h2>Artigo 2 – Identificação do vendedor</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, França<br />
                    Email: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Artigo 3 – Produto</h2>
                <p>
                    A oferta "Pro" é uma compra única (sem assinatura) ao preço de <strong>5,99€</strong>.
                    Dá acesso vitalício às seguintes funcionalidades:
                </p>
                <ul>
                    <li>Criação de links ilimitados</li>
                    <li>Suporte prioritário</li>
                    <li>Acesso antecipado a novas funcionalidades</li>
                </ul>

                <h2>Artigo 4 – Preço e pagamento</h2>
                <p>
                    O preço apresentado é de 5,99€ antes de impostos. Os impostos aplicáveis (IVA ou equivalente)
                    serão calculados no momento do pagamento com base no seu país de residência.
                    O montante final, incluindo impostos, será apresentado antes da confirmação. O pagamento
                    é efetuado online por cartão bancário através do nosso prestador Polar (utilizando Stripe).
                    O pagamento é seguro e está em conformidade com as normas PCI-DSS.
                </p>

                <h2>Artigo 5 – Entrega</h2>
                <p>
                    O acesso às funcionalidades Pro é entregue imediatamente após a confirmação do pagamento. Não se aplica qualquer prazo de entrega.
                </p>

                <h2>Artigo 6 – Direito de retratação</h2>
                <p>
                    De acordo com a lei do consumidor, o direito de retratação não pode ser exercido para contratos de fornecimento de conteúdo digital não fornecido em suporte material cuja execução tenha tido início após acordo prévio expresso do consumidor e renúncia expressa ao seu direito de retratação.
                </p>

                <h2>Artigo 7 – Garantia e responsabilidade</h2>
                <p>
                    O serviço é fornecido "tal como está". Comprometemo-nos a manter o serviço na medida do possível, mas não garantimos uma disponibilidade contínua de 100%.
                </p>

                <h2>Artigo 8 – Dados pessoais</h2>
                <p>
                    Os dados recolhidos são tratados de acordo com a nossa{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = 'privacy'; window.location.reload(); }}>
                        Política de Privacidade
                    </a>.
                </p>

                <h2>Artigo 9 – Lei aplicável</h2>
                <p>
                    Estas CGV estão sujeitas à lei francesa. Em caso de litígio, os tribunais franceses serão os únicos competentes.
                </p>
            </>
        ),
    },
    de: {
        mentions: () => (
            <>
                <h1>Impressum</h1>
                <p><em>Letzte Aktualisierung: Januar 2026</em></p>

                <h2>Herausgeber</h2>
                <p>
                    <strong>Bitek</strong> — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, Frankreich<br />
                    E-Mail: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Veröffentlichungsdirektor</h2>
                <p>Nicolas Cordeiro</p>

                <h2>Hosting</h2>
                <p>
                    <strong>Cloudflare, Inc.</strong><br />
                    101 Townsend Street<br />
                    San Francisco, CA 94107<br />
                    Vereinigte Staaten<br />
                    <a href="https://www.cloudflare.com" target="_blank" rel="noopener noreferrer">www.cloudflare.com</a>
                </p>

                <h2>Geistiges Eigentum</h2>
                <p>
                    Alle Inhalte dieser Website (Texte, Bilder, Logos, Quellcode) sind ausschließliches Eigentum von Bitek (Nicolas Cordeiro), sofern nicht anders angegeben. Jede Vervielfältigung, auch auszugsweise, ist ohne vorherige Genehmigung untersagt.
                </p>
            </>
        ),
        privacy: () => (
            <>
                <h1>Datenschutz</h1>
                <p><em>Letzte Aktualisierung: Januar 2026</em></p>

                <h2>Verantwortlicher</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    E-Mail: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Erhobene Daten</h2>
                <p>Wir erheben folgende Daten:</p>
                <ul>
                    <li><strong>E-Mail-Adresse</strong>: bereitgestellt über Google OAuth-Authentifizierung</li>
                    <li><strong>Linkdaten</strong>: gekürzte URLs, Ziele, vom Benutzer erstellte Beschreibungen</li>
                    <li><strong>Zahlungsdaten</strong>: verarbeitet durch Polar (via Stripe), wir speichern Ihre Kreditkarteninformationen nicht</li>
                </ul>

                <h2>Zwecke der Verarbeitung</h2>
                <ul>
                    <li>Erstellung und Verwaltung Ihres Benutzerkontos</li>
                    <li>Bereitstellung des URL-Kürzungsdienstes</li>
                    <li>Zahlungsabwicklung für das Pro-Angebot</li>
                    <li>Kommunikation bezüglich Ihres Kontos (Support, Abrechnung)</li>
                </ul>

                <h2>Rechtsgrundlage</h2>
                <p>
                    Die Verarbeitung Ihrer Daten erfolgt auf Grundlage der Vertragserfüllung (Dienstleistungserbringung) und Ihrer Einwilligung bei der Authentifizierung.
                </p>

                <h2>Aufbewahrungsdauer</h2>
                <p>
                    Ihre Daten werden so lange gespeichert, wie Ihr Konto aktiv ist. Im Falle einer Kontolöschung werden Ihre Daten innerhalb von 30 Tagen gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.
                </p>

                <h2>Ihre Rechte (DSGVO)</h2>
                <p>Gemäß der DSGVO haben Sie folgende Rechte:</p>
                <ul>
                    <li><strong>Auskunftsrecht</strong>: Kopie Ihrer Daten erhalten</li>
                    <li><strong>Recht auf Berichtigung</strong>: Ihre unrichtigen Daten korrigieren</li>
                    <li><strong>Recht auf Löschung</strong>: Ihre Daten löschen ("Recht auf Vergessenwerden")</li>
                    <li><strong>Recht auf Datenübertragbarkeit</strong>: Ihre Daten in einem strukturierten Format erhalten</li>
                    <li><strong>Widerspruchsrecht</strong>: der Verarbeitung Ihrer Daten widersprechen</li>
                </ul>
                <p>
                    Um diese Rechte auszuüben, kontaktieren Sie uns unter: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Cookies</h2>
                <p>
                    Diese Website verwendet nur technische Cookies, die für den Betrieb des Dienstes unerlässlich sind (Authentifizierung, Spracheinstellungen). Es werden keine Werbe- oder Tracking-Cookies verwendet.
                </p>

                <h2>Datenübermittlung</h2>
                <p>
                    Ihre Daten können auf Server außerhalb der Europäischen Union übertragen werden (Cloudflare, Stripe). Diese Übermittlungen sind durch geeignete Garantien abgedeckt (Standardvertragsklauseln, Zertifizierungen).
                </p>

                <h2>Beschwerde</h2>
                <p>
                    Im Streitfall können Sie eine Beschwerde bei der CNIL einreichen: <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
                </p>
            </>
        ),
        cgv: () => (
            <>
                <h1>Allgemeine Geschäftsbedingungen (AGB)</h1>
                <p><em>Letzte Aktualisierung: Januar 2026</em></p>

                <h2>Artikel 1 – Gegenstand</h2>
                <p>
                    Diese Allgemeinen Geschäftsbedingungen (AGB) regeln den Verkauf des "Pro"-Angebots des ShortURL-Dienstes, angeboten von Nicolas Cordeiro, Einzelunternehmer.
                </p>

                <h2>Artikel 2 – Verkäuferidentifikation</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, Frankreich<br />
                    E-Mail: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Artikel 3 – Produkt</h2>
                <p>
                    Das "Pro"-Angebot ist ein Einmalkauf (kein Abonnement) zum Preis von <strong>5,99€</strong>.
                    Es gewährt lebenslangen Zugriff auf folgende Funktionen:
                </p>
                <ul>
                    <li>Unbegrenzte Linkerstellung</li>
                    <li>Priorisierter Support</li>
                    <li>Vorabzugang zu neuen Funktionen</li>
                </ul>

                <h2>Artikel 4 – Preis und Zahlung</h2>
                <p>
                    Der angezeigte Preis beträgt 5,99€ vor Steuern. Die anfallenden Steuern (MwSt. oder
                    gleichwertig) werden bei der Zahlung entsprechend Ihres Wohnsitzlandes berechnet.
                    Der Endbetrag inklusive Steuern wird vor der Bestätigung angezeigt. Die Zahlung
                    erfolgt online per Kreditkarte über unseren Anbieter Polar (unter Verwendung von Stripe).
                    Die Zahlung ist sicher und PCI-DSS-konform.
                </p>

                <h2>Artikel 5 – Lieferung</h2>
                <p>
                    Der Zugang zu den Pro-Funktionen wird unmittelbar nach Zahlungsbestätigung bereitgestellt. Es gilt keine Lieferfrist.
                </p>

                <h2>Artikel 6 – Widerrufsrecht</h2>
                <p>
                    Gemäß Verbraucherschutzgesetz kann das Widerrufsrecht bei Verträgen über die Lieferung von digitalen Inhalten, die nicht auf einem körperlichen Datenträger geliefert werden, nicht ausgeübt werden, wenn die Ausführung mit der ausdrücklichen vorherigen Zustimmung des Verbrauchers und seiner ausdrücklichen Bestätigung über den Verlust seines Widerrufsrechts begonnen hat.
                </p>

                <h2>Artikel 7 – Gewährleistung und Haftung</h2>
                <p>
                    Der Dienst wird "wie besehen" bereitgestellt. Wir verpflichten uns, den Dienst so weit wie möglich aufrechtzuerhalten, garantieren jedoch keine 100%ige kontinuierliche Verfügbarkeit.
                </p>

                <h2>Artikel 8 – Personenbezogene Daten</h2>
                <p>
                    Die erhobenen Daten werden gemäß unserer{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = 'privacy'; window.location.reload(); }}>
                        Datenschutzerklärung
                    </a>
                    verarbeitet.
                </p>

                <h2>Artikel 9 – Anwendbares Recht</h2>
                <p>
                    Diese AGB unterliegen französischem Recht. Im Streitfall sind ausschließlich die französischen Gerichte zuständig.
                </p>
            </>
        ),
    },
    ar: {
        mentions: () => (
            <>
                <h1>الإشعار القانوني</h1>
                <p><em>آخر تحديث: يناير 2026</em></p>

                <h2>ناشر الموقع</h2>
                <p>
                    <strong>Bitek</strong> — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, France<br />
                    البريد الإلكتروني: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>مدير النشر</h2>
                <p>Nicolas Cordeiro</p>

                <h2>الاستضافة</h2>
                <p>
                    <strong>Cloudflare, Inc.</strong><br />
                    101 Townsend Street<br />
                    San Francisco, CA 94107<br />
                    United States<br />
                    <a href="https://www.cloudflare.com" target="_blank" rel="noopener noreferrer">www.cloudflare.com</a>
                </p>

                <h2>الملكية الفكرية</h2>
                <p>
                    جميع المحتويات على هذا الموقع (نصوص، صور، شعارات، كود المصدر) هي ملكية حصرية لـ Bitek (Nicolas Cordeiro)، ما لم يذكر خلاف ذلك. يُحظر أي نسخ، ولو جزئيًا، دون إذن مسبق.
                </p>
            </>
        ),
        privacy: () => (
            <>
                <h1>سياسة الخصوصية</h1>
                <p><em>آخر تحديث: يناير 2026</em></p>

                <h2>مراقب البيانات</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    البريد الإلكتروني: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>البيانات التي تم جمعها</h2>
                <p>نحن نجمع البيانات التالية:</p>
                <ul>
                    <li><strong>عنوان البريد الإلكتروني</strong>: يتم توفيره عبر مصادقة Google OAuth</li>
                    <li><strong>بيانات الرابط</strong>: عناوين URL المختصرة، الوجهات، الأوصاف التي أنشأها المستخدم</li>
                    <li><strong>بيانات الدفع</strong>: المعالجة بواسطة Polar (عبر Stripe)، نحن لا نخزن معلومات بطاقتك الائتمانية</li>
                </ul>

                <h2>أغراض المعالجة</h2>
                <ul>
                    <li>إنشاء وإدارة حساب المستخدم الخاص بك</li>
                    <li>توفير خدمة اختصار عناوين URL</li>
                    <li>معالجة المدفوعات لعرض Pro</li>
                    <li>التواصل المتعلق بحسابك (الدعم، الفواتير)</li>
                </ul>

                <h2>الأساس القانوني</h2>
                <p>
                    تستند معالجة بياناتك إلى تنفيذ العقد (توفير الخدمة) وموافقتك أثناء المصادقة.
                </p>

                <h2>فترة الاحتفاظ</h2>
                <p>
                    يتم الاحتفاظ ببياناتك طالما أن حسابك نشط. في حالة حذف الحساب، سيتم مسح بياناتك في غضون 30 يومًا، ما لم تكن هناك التزامات قانونية للاحتفاظ بها.
                </p>

                <h2>حقوقك (لائحة حماية البيانات العامة - GDPR)</h2>
                <p>وفقًا للائحة حماية البيانات العامة، لديك الحقوق التالية:</p>
                <ul>
                    <li><strong>حق الوصول</strong>: الحصول على نسخة من بياناتك</li>
                    <li><strong>حق التصحيح</strong>: تصحيح بياناتك غير الدقيقة</li>
                    <li><strong>حق المحو</strong>: حذف بياناتك ("الحق في النسيان")</li>
                    <li><strong>حق نقل البيانات</strong>: استلام بياناتك بتنسيق منظم</li>
                    <li><strong>حق الاعتراض</strong>: الاعتراض على معالجة بياناتك</li>
                </ul>
                <p>
                    لممارسة هذه الحقوق، اتصل بنا على: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>ملفات تعريف الارتباط (Cookies)</h2>
                <p>
                    يستخدم هذا الموقع فقط ملفات تعريف الارتباط الفنية الضرورية لتشغيل الخدمة (المصادقة، تفضيلات اللغة). لا يتم استخدام ملفات تعريف ارتباط إعلانية أو تتبع.
                </p>

                <h2>نقل البيانات</h2>
                <p>
                    قد يتم نقل بياناتك إلى خوادم تقع خارج الاتحاد الأوروبي (Cloudflare, Stripe). تغطي هذه التحويلات ضمانات مناسبة (بنود تعاقدية قياسية، شهادات).
                </p>

                <h2>الشكاوى</h2>
                <p>
                    في حالة النزاع، يمكنك تقديم شكوى إلى CNIL: <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
                </p>
            </>
        ),
        cgv: () => (
            <>
                <h1>شروط البيع</h1>
                <p><em>آخر تحديث: يناير 2026</em></p>

                <h2>المادة 1 – الموضوع</h2>
                <p>
                    تحكم شروط البيع هذه (CGV) بيع عرض "Pro" لخدمة ShortURL، المقترحة من قبل Nicolas Cordeiro، رائد أعمال فردي.
                </p>

                <h2>المادة 2 – تحديد البائع</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, France<br />
                    البريد الإلكتروني: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>المادة 3 – المنتج</h2>
                <p>
                    عرض "Pro" هو عملية شراء لمرة واحدة (بدون اشتراك) بسعر <strong>5.99€</strong>.
                    يمنح الوصول مدى الحياة إلى الميزات التالية:
                </p>
                <ul>
                    <li>إنشاء روابط غير محدودة</li>
                    <li>دعم ذو أولوية</li>
                    <li>الوصول المبكر إلى الميزات الجديدة</li>
                </ul>

                <h2>المادة 4 – السعر والدفع</h2>
                <p>
                    السعر المعروض هو 5.99€ قبل الضرائب. ستُحسب الضرائب المطبقة (ضريبة القيمة المضافة أو ما يعادلها)
                    عند الدفع بناءً على بلد إقامتك. سيتم عرض المبلغ النهائي شاملاً الضرائب قبل التأكيد.
                    يتم الدفع عبر الإنترنت بواسطة بطاقة ائتمان عبر مزودنا Polar (باستخدام Stripe).
                    الدفع آمن ومتوافق مع معايير PCI-DSS.
                </p>

                <h2>المادة 5 – التسليم</h2>
                <p>
                    يتم تسليم الوصول إلى ميزات Pro فور تأكيد الدفع. لا ينطبق أي وقت تسليم.
                </p>

                <h2>المادة 6 – حق الانسحاب</h2>
                <p>
                    وفقًا لقانون المستهلك، لا يمكن ممارسة حق الانسحاب للعقود الخاصة بتوريد المحتوى الرقمي غير المورد على وسيط مادي والذي بدأ تنفيذه بعد موافقة صريحة مسبقة من المستهلك وتنازل صريح عن حقه في الانسحاب.
                </p>

                <h2>المادة 7 – الضمان والمسؤولية</h2>
                <p>
                    يتم تقديم الخدمة "كما هي". نحن ملتزمون بالحفاظ على الخدمة قدر الإمكان، لكننا لا نضمن توفرًا مستمرًا بنسبة 100٪.
                </p>

                <h2>المادة 8 – البيانات الشخصية</h2>
                <p>
                    تتم معالجة البيانات التي تم جمعها وفقًا لـ{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = 'privacy'; window.location.reload(); }}>
                        سياسة الخصوصية
                    </a>
                    الخاصة بنا.
                </p>

                <h2>المادة 9 – القانون الواجب التطبيق</h2>
                <p>
                    تخضع هذه الشروط للقانون الفرنسي. في حالة النزاع، ستكون المحاكم الفرنسية هي المختصة وحدها.
                </p>
            </>
        ),
    },
    zh: {
        mentions: () => (
            <>
                <h1>法律声明</h1>
                <p><em>最后更新：2026年1月</em></p>

                <h2>网站发布者</h2>
                <p>
                    <strong>Bitek</strong> — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, France<br />
                    邮箱: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>出版总监</h2>
                <p>Nicolas Cordeiro</p>

                <h2>托管</h2>
                <p>
                    <strong>Cloudflare, Inc.</strong><br />
                    101 Townsend Street<br />
                    San Francisco, CA 94107<br />
                    United States<br />
                    <a href="https://www.cloudflare.com" target="_blank" rel="noopener noreferrer">www.cloudflare.com</a>
                </p>

                <h2>知识产权</h2>
                <p>
                    本网站的所有内容（文本、图像、徽标、源代码）均为 Bitek (Nicolas Cordeiro) 的独家财产，除非另有说明。未经事先授权，禁止任何复制，即使是部分复制。
                </p>
            </>
        ),
        privacy: () => (
            <>
                <h1>隐私政策</h1>
                <p><em>最后更新：2026年1月</em></p>

                <h2>数据控制者</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    邮箱: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>收集的数据</h2>
                <p>我们收集以下数据：</p>
                <ul>
                    <li><strong>电子邮件地址</strong>：通过 Google OAuth 身份验证提供</li>
                    <li><strong>链接数据</strong>：用户创建的缩短 URL、目标地址、描述</li>
                    <li><strong>支付数据</strong>：由 Polar（通过 Stripe）处理，我们不存储您的信用卡信息</li>
                </ul>

                <h2>处理目的</h2>
                <ul>
                    <li>创建和管理您的用户帐户</li>
                    <li>提供 URL 缩短服务</li>
                    <li>处理 Pro 优惠的付款</li>
                    <li>与您的帐户相关的沟通（支持、计费）</li>
                </ul>

                <h2>法律依据</h2>
                <p>
                    您的数据处理基于合同的履行（提供服务）以及您在身份验证时的同意。
                </p>

                <h2>保留期限</h2>
                <p>
                    只要您的帐户处于活动状态，您的数据就会被保留。如果帐户被删除，您的数据将在 30 天内被清除，除非有法律规定的保留义务。
                </p>

                <h2>您的权利 (GDPR)</h2>
                <p>根据 GDPR，您拥有以下权利：</p>
                <ul>
                    <li><strong>访问权</strong>：获取您的数据副本</li>
                    <li><strong>更正权</strong>：更正您的不准确数据</li>
                    <li><strong>删除权</strong>：删除您的数据（“被遗忘权”）</li>
                    <li><strong>数据可携权</strong>：以结构化格式接收您的数据</li>
                    <li><strong>反对权</strong>：反对处理您的数据</li>
                </ul>
                <p>
                    要行使这些权利，请联系我们：<a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>Cookie</h2>
                <p>
                    本网站仅使用服务运行所必需的技术 Cookie（身份验证、语言首选项）。不使用广告或跟踪 Cookie。
                </p>

                <h2>数据传输</h2>
                <p>
                    您的数据可能会传输到位于欧盟境外的服务器（Cloudflare、Stripe）。这些传输由适当的保障措施（标准合同条款、认证）涵盖。
                </p>

                <h2>投诉</h2>
                <p>
                    如有争议，您可以向 CNIL 提出投诉：<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
                </p>
            </>
        ),
        cgv: () => (
            <>
                <h1>销售条款</h1>
                <p><em>最后更新：2026年1月</em></p>

                <h2>第 1 条 – 对象</h2>
                <p>
                    本销售条款 (CGV) 规范由个体企业家 Nicolas Cordeiro 提供的 ShortURL 服务“Pro”优惠的销售。
                </p>

                <h2>第 2 条 – 卖方身份</h2>
                <p>
                    Bitek — Nicolas Cordeiro<br />
                    SIREN: 884314147<br />
                    8 Impasse des Luaps, 92000 Nanterre, France<br />
                    邮箱: <a href="mailto:bitek.fr@gmail.com">bitek.fr@gmail.com</a>
                </p>

                <h2>第 3 条 – 产品</h2>
                <p>
                    “Pro”优惠为一次性购买（无订阅），价格为 <strong>5.99€</strong>。
                    它提供终身访问以下功能：
                </p>
                <ul>
                    <li>无限链接创建</li>
                    <li>优先支持</li>
                    <li>抢先体验新功能</li>
                </ul>

                <h2>第 4 条 – 价格和付款</h2>
                <p>
                    显示的价格为 5.99€ 税前价。适用税费（增值税或同等税费）将在结账时根据您的居住国家/地区计算。
                    最终金额（含税）将在确认前显示。通过我们的提供商 Polar（使用 Stripe）在线通过信用卡付款。
                    付款安全且符合 PCI-DSS 标准。
                </p>

                <h2>第 5 条 – 交付</h2>
                <p>
                    付款确认后立即交付 Pro 功能的访问权限。不适用任何交货时间。
                </p>

                <h2>第 6 条 – 撤回权</h2>
                <p>
                    根据消费者法，对于未在有形介质上提供的数字内容供应合同，如果执行已在消费者事先明确同意并明确放弃撤回权后开始，则不能行使撤回权。
                </p>

                <h2>第 7 条 – 保证和责任</h2>
                <p>
                    服务按“原样”提供。我们致力于尽可能维护服务，但不保证 100% 的持续可用性。
                </p>

                <h2>第 8 条 – 个人数据</h2>
                <p>
                    收集的数据按照我们的{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = 'privacy'; window.location.reload(); }}>
                        隐私政策
                    </a>
                    进行处理。
                </p>

                <h2>第 9 条 – 适用法律</h2>
                <p>
                    本条款受法国法律管辖。如有争议，法国法院拥有唯一管辖权。
                </p>
            </>
        ),
    },
};

export default legalContent;
